import { FC, useState, useEffect } from 'react'
import { useAuth } from '../../auth'
import { toast } from 'react-toastify'
import {
  getCompanyGroupAdmins,
  getSubCompanyAdmins,
  getVesselsByCompany,
  getAllInventoryCategoriesForCompany,
  createAccountingAccount,
} from '../core/_requests'
import {
  CompanyGroupAdmin,
  SubCompanyAdmin,
  Vessel,
  InventoryCategory,
} from '../core/_models'
import { useProcurementPermissions } from '../core/permissionAccess'
import { PROCUREMENT_PERMS } from '../core/permissions'
import { libraryUiScope, libraryVesselScopeLocked } from '../core/procurementLibraryUiScope'
import { procurementTenantCgaId } from '../core/procurementTenantContext'
import SearchableSelect from '../../../shared/components/SearchableSelect'

interface AccountingCodeModalProps {
  visible: boolean
  onClose: () => void
  onSubmit: () => void
  vesselId: number
  embedded?: boolean
  stayOpenOnSubmit?: boolean
}

const AccountingCodeModal: FC<AccountingCodeModalProps> = ({
  visible,
  onClose,
  onSubmit,
  vesselId,
  embedded = false,
  stayOpenOnSubmit = false,
}) => {
  const { currentUser } = useAuth()
  const { getScope } = useProcurementPermissions()
  const libraryScope = libraryUiScope(
    getScope,
    PROCUREMENT_PERMS.LIBRARY_ACCOUNTING_MANAGE,
    PROCUREMENT_PERMS.LIBRARY_ACCOUNTING_VIEW,
  )
  const vesselScopeLocked = libraryVesselScopeLocked(
    getScope,
    PROCUREMENT_PERMS.LIBRARY_ACCOUNTING_MANAGE,
    PROCUREMENT_PERMS.LIBRARY_ACCOUNTING_VIEW,
  )
  const libraryScopeAll = libraryScope === 'ALL'
  const tenantCgaId = procurementTenantCgaId(currentUser)

  const [formData, setFormData] = useState({
    company: '',
    subCompany: '',
    accountCodes: '',
    vesselId: '',
    categoryId: ''
  })
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [categories, setCategories] = useState<InventoryCategory[]>([])
  const [isLoadingCategories, setIsLoadingCategories] = useState(false)

  // New state for dropdown data
  const [companies, setCompanies] = useState<CompanyGroupAdmin[]>([])
  const [subCompanies, setSubCompanies] = useState<SubCompanyAdmin[]>([])
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(false)

  // Load categories when modal opens, company changes, or vessel type changes
  useEffect(() => {
    if (visible) {
      loadCategories()
      // Load companies for superadmin
      if (libraryScopeAll) {
        loadCompanies()
      } else {
        // For non-superadmin users, load their company's vessels to get vessel types
        loadVesselsForCurrentUser()
      }
    }
  }, [visible, formData.company, formData.vesselId])

  // Load vessels for current user (non-superadmin)
  const loadVesselsForCurrentUser = async () => {
    try {
      // Get the current user's company username
      const companyUsername = (currentUser as any)?.companyGroupAdmin?.username || (currentUser as any)?.username
      console.log('AccountingCodeModal - Loading vessels for current user:', companyUsername)
      if (companyUsername) {
        const vesselsData = await getVesselsByCompany(companyUsername)
        console.log('AccountingCodeModal - Vessels loaded for current user:', vesselsData)
        setVessels(vesselsData)
      }
    } catch (error) {
      console.error('AccountingCodeModal - Error loading vessels for current user:', error)
      setVessels([])
    }
  }

  // Load subcompanies when company is selected
  useEffect(() => {
    if (formData.company) {
      loadSubCompanies(parseInt(formData.company))
    } else {
      setSubCompanies([])
    }
  }, [formData.company])

  // Load vessels when company is selected
  useEffect(() => {
    if (formData.company) {
      const selectedCompany = companies.find(c => c.id === parseInt(formData.company))
      if (selectedCompany) {
        loadVessels(selectedCompany.username)
      }
    } else {
      setVessels([])
    }
  }, [formData.company, companies])

  const loadCategories = async () => {
    setIsLoadingCategories(true)
    try {
      let cgaIdToUse: number | undefined
      if (libraryScopeAll) {
        cgaIdToUse = formData.company ? parseInt(formData.company, 10) : undefined
      } else {
        cgaIdToUse = tenantCgaId
      }

      if (!cgaIdToUse || Number.isNaN(cgaIdToUse)) {
        setCategories([])
        return
      }

      console.log('AccountingCodeModal - Loading categories with cgaId:', cgaIdToUse)

      const vesselIdParam = formData.vesselId ? Number(formData.vesselId) : undefined
      const data = await getAllInventoryCategoriesForCompany(cgaIdToUse, vesselIdParam)
      console.log('AccountingCodeModal - Categories loaded:', data)
      setCategories(data || [])

      // Reset category selection if current selection is not valid
      if (formData.categoryId && !data?.find(cat => cat.id.toString() === formData.categoryId)) {
        setFormData(prev => ({ ...prev, categoryId: '' }))
      }
    } catch (error) {
      console.error('AccountingCodeModal - Error loading categories:', error)
      setCategories([])
      // Show user-friendly error message
      toast.error('Failed to load categories. Please check your connection and try again.')
    } finally {
      setIsLoadingCategories(false)
    }
  }

  const loadCompanies = async () => {
    setIsLoadingDropdowns(true)
    try {
      const companiesData = await getCompanyGroupAdmins()
      setCompanies(companiesData)
    } catch (error) {
      console.error('Error loading companies:', error)
    } finally {
      setIsLoadingDropdowns(false)
    }
  }

  const loadSubCompanies = async (companyId: number) => {
    try {
      const subCompaniesData = await getSubCompanyAdmins(companyId)
      setSubCompanies(subCompaniesData)
    } catch (error) {
      console.error('Error loading sub companies:', error)
      setSubCompanies([])
    }
  }

  const loadVessels = async (companyUsername: string) => {
    try {
      const vesselsData = await getVesselsByCompany(companyUsername)
      setVessels(vesselsData)
    } catch (error) {
      console.error('Error loading vessels:', error)
      setVessels([])
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }

    // If vessel changed, reset category selection and reload categories
    if (name === 'vesselId' && value !== formData.vesselId) {
      setFormData(prev => ({ ...prev, categoryId: '' }))
    }
  }

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}
    if (!formData.accountCodes.trim()) newErrors.accountCodes = 'Account Code is required'
    // COMMENTED OUT FOR TESTING - Category validation
    // if (!formData.categoryId) newErrors.categoryId = 'Category is required'

    // Only validate company selection if user is superadmin
    if (libraryScopeAll && !formData.company) {
      newErrors.company = 'Company is required'
    }
    if (vesselScopeLocked && !formData.vesselId) {
      newErrors.vesselId = 'Vessel is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const resetFormState = () => {
    setFormData({ company: '', subCompany: '', accountCodes: '', vesselId: '', categoryId: '' })
    setErrors({})
    setCategories([])
    setCompanies([])
    setSubCompanies([])
    setVessels([])
    setIsSubmitting(false)
  }

  const handleSubmit = async () => {
    if (!validateForm()) return
    setIsSubmitting(true)
    try {
      let cgaIdToUse: number
      if (libraryScopeAll) {
        if (!formData.company) {
          toast.error('Please select a company')
          setIsSubmitting(false)
          return
        }
        cgaIdToUse = parseInt(formData.company, 10)
      } else {
        if (!tenantCgaId) {
          toast.error('Unable to resolve company scope for this user')
          setIsSubmitting(false)
          return
        }
        cgaIdToUse = tenantCgaId
      }

      const selectedVessel = formData.vesselId
        ? vessels.find((v) => v.id === Number(formData.vesselId))
        : undefined
      const payload = {
        accountCodes: formData.accountCodes,
        cgaid: { id: cgaIdToUse },
        vesselId: formData.vesselId ? Number(formData.vesselId) : undefined,
        vesselType: selectedVessel?.vesselType,
        // COMMENTED OUT FOR TESTING - Category field
        // category: { id: parseInt(formData.categoryId) }
      }

      console.log('AccountingCodeModal - Submitting accounting account with payload:', payload)
      // Type assertion to bypass TypeScript error for testing without category
      const result = await createAccountingAccount(payload as any)
      console.log('AccountingCodeModal - Accounting account created successfully:', result)

      toast.success('Accounting Code added successfully!')
      onSubmit()
      if (stayOpenOnSubmit) {
        resetFormState()
      } else {
        handleClose()
      }
    } catch (error: any) {
      console.error('AccountingCodeModal - Error adding accounting code:', error)
      console.error('AccountingCodeModal - Error details:', error.response?.data)
      toast.error('Failed to add accounting code')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    resetFormState()
    onClose()
  }

  if (!visible) return null

  const modalContent = (
    <div
      className='modal-content bg-white'
      style={{
        color: '#181C32',
        height: embedded ? '100%' : undefined,
        borderRadius: embedded ? '12px' : undefined,
        boxShadow: embedded ? '0 12px 40px rgba(15,23,42,0.06)' : undefined,
        border: embedded ? '1px solid #E9EEF5' : undefined,
      }}
    >
      <div className='modal-header'>
        <h5 className='modal-title'>Add Accounting Code</h5>
        <button type='button' className='btn-close' onClick={handleClose}></button>
      </div>
      <div className='modal-body' style={{overflowY: embedded ? 'auto' : undefined}}>
        <div className='row g-3'>
          {/* Company dropdown - only for superadmin */}
          {libraryScopeAll && (
            <div className='col-md-6'>
              <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Company</label>
              <SearchableSelect
                className={`form-select ${errors.company ? 'is-invalid' : ''}`}
                name='company'
                value={formData.company}
                onChange={handleInputChange}
                disabled={isLoadingDropdowns}
              >
                <option value=''>Select Company</option>
                {companies.map(company => (
                  <option key={company.id} value={company.id}>{company.name}</option>
                ))}
              </SearchableSelect>
              {errors.company && <div className='invalid-feedback'>{errors.company}</div>}
            </div>
          )}

          {/* Subcompany dropdown - only for superadmin when company is selected */}
          {libraryScopeAll && formData.company && (
            <div className='col-md-6'>
              <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Sub Company</label>
              <SearchableSelect
                className='form-select'
                name='subCompany'
                value={formData.subCompany}
                onChange={handleInputChange}
              >
                <option value=''>Select Sub Company (Optional)</option>
                {subCompanies.map(subCompany => (
                  <option key={subCompany.id} value={subCompany.id}>{subCompany.name}</option>
                ))}
              </SearchableSelect>
            </div>
          )}

          <div className='col-12'>
            <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Account Code</label>
            <input
              type='text'
              className={`form-control ${errors.accountCodes ? 'is-invalid' : ''}`}
              name='accountCodes'
              value={formData.accountCodes}
              onChange={handleInputChange}
              placeholder='e.g., 6000-EXP-01'
              style={{ color: '#000' }}
            />
            {errors.accountCodes && <div className='invalid-feedback'>{errors.accountCodes}</div>}
          </div>
          <div className='col-md-6'>
            <label
              className={`form-label fw-semibold fs-6 mb-2 ${vesselScopeLocked ? 'required' : ''}`}
              style={{ color: '#181C32' }}
            >
              {vesselScopeLocked ? 'Vessel *' : 'Vessel (Optional)'}
            </label>
            <SearchableSelect
              className={`form-select ${errors.vesselId ? 'is-invalid' : ''}`}
              name='vesselId'
              value={formData.vesselId}
              onChange={handleInputChange}
              disabled={libraryScopeAll ? !formData.company : false}
            >
              <option value=''>{vesselScopeLocked ? 'Select Vessel' : 'All Vessels (shared)'}</option>
              {libraryScopeAll ? (
                formData.company ? (
                  vessels.map(vessel => (
                    <option key={vessel.id} value={vessel.id}>{vessel.fleet_name}</option>
                  ))
                ) : (
                  <option disabled>Please select a company first</option>
                )
              ) : (
                vessels.length > 0 ? (
                  vessels.map(vessel => (
                    <option key={vessel.id} value={vessel.id}>{vessel.fleet_name}</option>
                  ))
                ) : (
                  <option disabled>No vessels found</option>
                )
              )}
            </SearchableSelect>
            {errors.vesselId && <div className='invalid-feedback'>{errors.vesselId}</div>}
          </div>
          {/* COMMENTED OUT FOR TESTING - Category dropdown */}
          {/* 
          <div className='col-md-6'>
            <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Category</label>
            <SearchableSelect
              className={`form-select ${errors.categoryId ? 'is-invalid' : ''}`}
              name='categoryId'
              value={formData.categoryId}
              onChange={handleInputChange}
              disabled={isLoadingCategories}
            >
              <option value=''>
                {isLoadingCategories ? 'Loading categories...' : 'Select Category'}
              </option>
              {categories.length === 0 && !isLoadingCategories && (
                <option value='' disabled>No categories found - Create a category first</option>
              )}
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </SearchableSelect>
            {errors.categoryId && <div className='invalid-feedback'>{errors.categoryId}</div>}
            {categories.length === 0 && !isLoadingCategories && (
              <small className='text-warning'>
                <i className='fas fa-exclamation-triangle me-1'></i>
                No categories found. Please create a category first in the Category tab.
              </small>
            )}
          </div>
          */}
        </div>
      </div>
      <div className='modal-footer'>
        <button type='button' className='btn btn-light btn-sm' onClick={handleClose} disabled={isSubmitting}>Cancel</button>
        <button type='button' className='btn btn_primary' onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Adding...' : 'Add Accounting Code'}
        </button>
      </div>
    </div>
  )

  if (embedded) {
    return (
      <div
        className='h-100 d-flex flex-column'
        style={{ background: '#F8FAFC', padding: '12px', overflow: 'auto' }}
      >
        {modalContent}
      </div>
    )
  }

  return (
    <div className="modal fade show d-flex align-items-center justify-content-center" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 1050 }}>
      <div className='modal-dialog modal-lg modal-dialog-centered' role='document'>
        {modalContent}
      </div>
    </div>
  )
}

export default AccountingCodeModal;
